/**
 * Admin Orders API Service
 * Handles order management operations
 */

import { get, post, patch } from "../client";
import { BookingRecord, PaginatedResponse } from "../../../types";

// === Request Types ===

export interface GetOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateOrderStatusRequest {
  status: "pending" | "paid" | "completed" | "cancelled";
  paymentMethod?: string;
  paymentReference?: string;
}

// === Response Types ===

export interface ApiOrder {
  id: string;
  booking_code: string;
  participant: number;
  visit_date: string;
  payment_status: string;
  payment_method: string | null;
  is_bulk_discount: boolean;
  bulk_discount_percent: number;
  price_snap: number;
  discount_snap: number;
  commission_snap: number;
  service_fee_snap: number;
  gross_amount: number;
  total_amount: number;
  net_income: number;
  check_in_at: string | null;
  created_at: string;
  customer: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
  };
  ticket: {
    id: string;
    name: string;
    type: string;
  };
  voucher?: {
    id: string;
    code: string;
    partner?: {
      id: string;
      user?: {
        name: string;
      };
    };
  } | null;
}

interface OrdersResponse {
  success: boolean;
  data: {
    data: ApiOrder[];
    meta: {
      total: number;
      perPage: number;
      currentPage: number;
      lastPage: number;
    };
  };
}

interface OrderResponse {
  success: boolean;
  data: ApiOrder;
}

export interface TodayVisitorsSummary {
  totalBookings: number;
  totalVisitors: number;
  checkedIn: number;
  notCheckedIn: number;
}

interface TodayVisitorsResponse {
  success: boolean;
  data: any;
}

export interface CheckInResult {
  success: boolean;
  message: string;
  requirePaymentConfirmation?: boolean;
  data?: {
    bookingCode: string;
    customer: string;
    phone?: string;
    ticket: string;
    participant: number;
    checkInAt?: string;
    totalAmount?: number;
    visitDate?: string;
    paymentMethod?: string;
  };
}

// === Transform Functions ===

function transformOrder(apiOrder: ApiOrder): BookingRecord {
  // Jika bulk discount aktif, discount_snap adalah diskon efektif (sudah dipotong service fee)
  // Jika tidak bulk, discount_snap adalah voucher discount
  const isBulk = apiOrder.is_bulk_discount;
  // Gunakan discount_snap langsung dari database (sudah berisi diskon efektif)
  const bulkDiscountAmount = isBulk ? apiOrder.discount_snap : 0;
  const voucherDiscountAmount = isBulk ? 0 : apiOrder.discount_snap;

  return {
    id: apiOrder.id,
    code: apiOrder.booking_code,
    customerName: apiOrder.customer.full_name,
    email: apiOrder.customer.email,
    phone: apiOrder.customer.phone,
    date: apiOrder.visit_date,
    category: apiOrder.participant >= 30 ? "School" : "Personal",
    ticketId: apiOrder.ticket.id,
    ticketType: apiOrder.ticket.name,
    pax: apiOrder.participant,
    totalPrice: apiOrder.total_amount,
    status: transformStatus(apiOrder.payment_status),
    paymentMethod:
      (apiOrder.payment_method?.toUpperCase() as "QRIS" | "LOKET") || "QRIS",
    bookingDate: apiOrder.created_at,
    checkInTime: apiOrder.check_in_at || undefined,
    voucherCode: isBulk ? undefined : apiOrder.voucher?.code,
    affiliateName: isBulk ? undefined : apiOrder.voucher?.partner?.user?.name,
    grossPrice: apiOrder.gross_amount,
    voucherDiscount: voucherDiscountAmount,
    bulkDiscount: bulkDiscountAmount,
    serviceFee: apiOrder.service_fee_snap,
    affiliateFee: apiOrder.commission_snap,
    netIncome: apiOrder.net_income,
  };
}

function transformStatus(status: string): BookingRecord["status"] {
  const statusMap: Record<string, BookingRecord["status"]> = {
    pending: "PENDING",
    paid: "PAID",
    completed: "COMPLETED",
    cancelled: "CANCELLED",
  };
  return statusMap[status.toLowerCase()] || "PENDING";
}

// === API Functions ===

/**
 * Get orders with pagination and filters
 */
export async function getOrders(
  params: GetOrdersParams = {}
): Promise<PaginatedResponse<BookingRecord>> {
  const response = await get<OrdersResponse>("/admin/orders", {
    page: params.page,
    limit: params.limit,
    status: params.status,
    search: params.search,
    start_date: params.startDate,
    end_date: params.endDate,
  });

  return {
    data: response.data.data.map(transformOrder),
    meta: {
      total: response.data.meta.total,
      perPage: response.data.meta.perPage,
      currentPage: response.data.meta.currentPage,
      lastPage: response.data.meta.lastPage,
      firstPage: 1,
    },
  };
}

/**
 * Get single order by ID
 */
export async function getOrder(id: string): Promise<BookingRecord> {
  const response = await get<OrderResponse>(`/admin/orders/${id}`);
  return transformOrder(response.data);
}

/**
 * Update order status
 */
export async function updateOrderStatus(
  id: string,
  data: UpdateOrderStatusRequest
): Promise<BookingRecord> {
  const response = await patch<OrderResponse>(
    `/admin/orders/${id}/status`,
    data
  );
  return transformOrder(response.data);
}

/**
 * Check-in a booking
 */
export async function checkInBooking(
  bookingCode: string,
  confirmPayment?: boolean
): Promise<CheckInResult> {
  return await post<CheckInResult>("/admin/orders/checkin", {
    bookingCode,
    confirmPayment,
  });
}

/**
 * Get today's visitors
 */
export async function getTodayVisitors(): Promise<{
  summary: TodayVisitorsSummary;
  visitors: BookingRecord[];
}> {
  const response = await get<TodayVisitorsResponse>("/admin/orders/today");

  const payload = response.data || {};

  if (payload.summary && Array.isArray(payload.visitors)) {
    const summary = payload.summary as Partial<TodayVisitorsSummary>;
    const visitors = payload.visitors as ApiOrder[];

    return {
      summary: {
        totalBookings: Number(summary.totalBookings || visitors.length || 0),
        totalVisitors: Number(summary.totalVisitors || 0),
        checkedIn: Number(summary.checkedIn || 0),
        notCheckedIn: Number(
          summary.notCheckedIn ||
          Math.max(Number(summary.totalVisitors || 0) - Number(summary.checkedIn || 0), 0)
        ),
      },
      visitors: visitors.map(transformOrder),
    };
  }

  const totalVisitors = Number(payload.totalVisitors || 0);
  const checkedIn = Number(payload.checkedIn || 0);

  return {
    summary: {
      totalBookings: 0,
      totalVisitors,
      checkedIn,
      notCheckedIn: Math.max(totalVisitors - checkedIn, 0),
    },
    visitors: [],
  };
}
export interface OrdersStats {
  pending: number;
  paid: number;
  completed: number;
  cancelled: number;
  total: number;
}

interface OrdersStatsResponse {
  success: boolean;
  data: OrdersStats;
}

/**
 * Get orders stats by status
 */
export async function getOrdersStats(): Promise<OrdersStats> {
  const response = await get<OrdersStatsResponse>("/admin/orders/stats");
  return response.data;
}
